<template>
    <!-- Covers the app rather than replacing it: the routed page stays mounted, so
         widening the window reveals it instantly with its data and form state intact,
         and crawlers still see the real page content underneath. -->
    <div
        v-if="isBlocked"
        ref="panel"
        :aria-label="title"
        aria-modal="true"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-white px-6"
        role="dialog"
        tabindex="-1"
    >
        <div class="max-w-md flex flex-col items-center gap-6">
            <!-- The brand mark leads, above the icon. VLogo is white-label aware, so a
                 partner's visitor sees their own mark here rather than Valur's. -->
            <v-logo
                v-if="showLogo"
                width-class="w-28"
            />
            <!-- The icon carries the way out: a device that can rotate into a supported
                 width gets the rotate glyph, everything else gets the monitor it's being
                 sent to. -->
            <v-empty
                :description="description"
                :icon="icon"
                size="lg"
                :title="title"
            />
        </div>
    </div>
</template>

<script>
    import { markRaw } from 'vue';
    import { mapGetters } from 'vuex';

    import { MIN_SUPPORTED_WIDTH } from '@/services/viewport';
    import { FLAG_LAYOUT_NARROW_VIEWPORT, isFeatureEnabled } from '@/store/feature_flags';
    import { isTenantPartner } from '@/services/advisoryPartner';
    import { trackSmallScreenGateLifted, trackSmallScreenGateShown } from '@/posthog_tracking';

    import VEmpty from '@/components/elements/VEmpty.vue';
    import VLogo from '@/components/VLogo.vue';

    // The 0.02px shaves the boundary so a viewport sitting exactly on the floor is never
    // gated, even at a fractional width (browser zoom, hidpi) where `max-width: floor`
    // would still match.
    const belowMinWidth = floor => `(max-width: ${floor - 0.02}px)`;

    // A device with no window to widen has one way out the gate that isn't "find a
    // computer": most phones are wider than the floor on their long side (844 on an
    // iPhone 14 against 748), so turning it sideways gets them in. Only offer that when
    // it's true — `screen` describes the device rather than the window, and browsers
    // disagree about whether they swap its dimensions on rotation, so the longest side is
    // the width landscape can actually give. An SE's 667 can't clear the floor either way,
    // and promising it a way in that doesn't exist is worse than saying nothing.
    //
    // The coarse-pointer test is what keeps the hint off a desktop with a narrow window,
    // where a 2560px screen passes the width test and rotating it is not the advice.
    const TOUCH_POINTER = '(hover: none) and (pointer: coarse)';
    const PORTRAIT = '(orientation: portrait)';

    // Routes that stay reachable below MIN_SUPPORTED_WIDTH. Two kinds:
    //
    // The auth entry points someone legitimately lands on from a phone — an emailed
    // activation or password-reset link, the login/sign-up forms — plus the two legal
    // pages those forms link out to. Gating them would mean a user can't even sign in to
    // learn they need a desktop.
    //
    // And the sign-up flow those entry points feed: `register*` and the `onboarding`
    // wizard behind it. Activate.vue sends every new user straight to the wizard, so
    // gating it would end the emailed-link journey one step short of an account — the
    // single place in the app where the gate could cost us the user rather than
    // inconvenience them. It's a single-column form flow; it works at phone widths.
    const EXEMPT_ROUTES = new Set([
        'activate',
        'clear',
        'forgot-password',
        'home',
        'login',
        'onboarding',
        'password-reset',
        'privacy-policy',
        'resend-activation',
        'terms-of-service',
    ]);

    // `register`, `register-advisor-link`, … — the whole family is public sign-up.
    // Mirrors the prefix check needsLogin() uses in router.js.
    const EXEMPT_ROUTE_PREFIX = 'register';

    // A visitor on a partner subdomain is in their advisor's product, not ours: VLogo
    // already shows the firm's mark, so naming Valur in the copy beside it would undo
    // the white-label. We deliberately say "the app" rather than the firm's own name —
    // a partner that hasn't uploaded a logo still falls back to the Valur mark, and
    // pairing that with the firm's name would read as an endorsement neither side made.
    //
    // `rotateDescription` stands in for `description` on a device that can turn into a
    // supported width: "widen your browser window" is not an action a phone has, and the
    // rotation leads because it's the one thing the user can do without leaving the page.
    const COPY = {
        valur: {
            title: 'Valur works best on a larger screen',
            description:
                'This page is designed for a desktop layout. Open Valur on a computer, ' +
                'or widen your browser window, to continue.',
            rotateDescription:
                'This page is designed for a desktop layout. Turn your device ' +
                'sideways to continue here, or open Valur on a computer.',
        },
        partner: {
            title: 'This app works best on a larger screen',
            description:
                'This page is designed for a desktop layout. Open the app on a computer, ' +
                'or widen your browser window, to continue.',
            rotateDescription:
                'This page is designed for a desktop layout. Turn your device ' +
                'sideways to continue here, or open the app on a computer.',
        },
    };

    export default {
        name: 'v-small-screen-gate',
        components: {
            VEmpty,
            VLogo,
        },
        emits: ['update:blocked'],
        data() {
            // Read synchronously so the very first render is already correct. Measuring in
            // mounted() — as breakpoints_mixin does — would flash this notice on desktop,
            // because its breakpoint is null until after the initial render.
            const mediaQuery = window.matchMedia(belowMinWidth(MIN_SUPPORTED_WIDTH));
            const orientationQuery = window.matchMedia(PORTRAIT);
            return {
                minSupportedWidth: MIN_SUPPORTED_WIDTH,
                isTooNarrow: mediaQuery.matches,
                isPortrait: orientationQuery.matches,
                // Neither of these can change under us: a pointer doesn't turn coarse
                // mid-session, and `screen` describes the device, so rotating swaps at
                // most which side it calls width. Read once.
                isTouchDevice: window.matchMedia(TOUCH_POINTER).matches,
                longestScreenSide: Math.max(window.screen?.width || 0, window.screen?.height || 0),
                // Set while the gate is up, so `Small Screen Gate Lifted` can report how
                // long it stood and isn't fired by the watcher's `immediate` run.
                blockedAt: null,
                // markRaw: these are live browser objects, not state to make reactive.
                mediaQuery: markRaw(mediaQuery),
                orientationQuery: markRaw(orientationQuery),
            };
        },
        computed: {
            ...mapGetters('marketing', ['brand_profile']),
            // Reading the getter directly rather than pulling in advisory_partner_mixin:
            // this component is mounted on every page and only needs the one boolean —
            // the mixin would also attach its white-label FontFace watcher, which
            // VBaseLayout already runs.
            isPartnerTenant() {
                return isTenantPartner(this.brand_profile);
            },
            copy() {
                return this.isPartnerTenant ? COPY.partner : COPY.valur;
            },
            // VLogo falls back to the Valur mark for a tenant that hasn't uploaded one
            // yet. That's right in the sidebar, beside other chrome — but here it would
            // be the only image on a full-screen panel whose copy deliberately avoids
            // naming us, and its alt text reads "Valur logo" to a screen reader on the
            // firm's own domain. Show nothing rather than the wrong brand.
            showLogo() {
                return !this.isPartnerTenant || Boolean(this.brand_profile?.logo_url);
            },
            title() {
                return this.copy.title;
            },
            description() {
                return this.canRotateIntoSupport ? this.copy.rotateDescription : this.copy.description;
            },
            icon() {
                return this.canRotateIntoSupport ? 'device-rotate' : 'monitor';
            },
            canRotateIntoSupport() {
                return this.isTouchDevice && this.isPortrait && this.longestScreenSide >= this.minSupportedWidth;
            },
            // Live, not read once at create: this flag is the rollout's kill switch, and
            // switching it off has to reach tabs that are already open (see the foreground
            // re-hydrate in store/feature_flags.js) — otherwise the one state that must be
            // instant, "stop gating people", is the one that waits for a reload. Reading it
            // late can't flash the gate on: the pre-mount bootstrap answers this flag, so
            // the first render already knows.
            isNarrowViewportEnabled() {
                return isFeatureEnabled(FLAG_LAYOUT_NARROW_VIEWPORT);
            },
            // The gate exists only while the narrow-viewport work is on. It shipped with
            // those layouts and rolls back with them, so with the flag off nothing here
            // blocks, tracks, or touches the body — the app is what production serves
            // today at every width.
            isBlocked() {
                return this.isNarrowViewportEnabled && this.isTooNarrow && !this.isExemptRoute;
            },
            isExemptRoute() {
                const name = this.$route?.name || '';
                return EXEMPT_ROUTES.has(name) || name.indexOf(EXEMPT_ROUTE_PREFIX) === 0;
            },
        },
        methods: {
            handleViewportChange(event) {
                this.isTooNarrow = event.matches;
            },
            // Rotating usually lifts the gate outright, but not on a tablet running the
            // browser in a split-screen half: still gated, now already sideways. Track the
            // orientation so the copy stops offering a rotation the user just performed.
            handleOrientationChange(event) {
                this.isPortrait = event.matches;
            },
            // Three ways the gate comes down, and they don't mean the same thing: the user
            // widened the window (the recovery this measures), they landed on an exempt
            // route while still too narrow, or the flag went off under them.
            liftReason() {
                if (!this.isNarrowViewportEnabled) return 'flag-off';
                return this.isTooNarrow ? 'exempt-route' : 'resized';
            },
            // How much traffic the floor turns away, and whether those users recover. Both
            // events carry the `min_supported_width` in force, so a series stays readable
            // across a move of the floor instead of silently mixing two of them.
            trackGateChange(blocked) {
                if (blocked) {
                    this.blockedAt = Date.now();
                    trackSmallScreenGateShown({
                        width: window.innerWidth,
                        height: window.innerHeight,
                        minSupportedWidth: this.minSupportedWidth,
                        routeName: this.$route?.name,
                        isPartner: this.isPartnerTenant,
                        // Whether this user was shown a way in or sent away, which is the
                        // difference between a gate that costs a session and one that costs
                        // a rotation.
                        canRotate: this.canRotateIntoSupport,
                    });
                    return;
                }
                // The watcher runs `immediate`, so it fires with blocked=false on every
                // desktop load. Only a gate that actually stood can be lifted.
                if (!this.blockedAt) return;
                const secondsBlocked = Math.round((Date.now() - this.blockedAt) / 1000);
                this.blockedAt = null;
                trackSmallScreenGateLifted({
                    width: window.innerWidth,
                    minSupportedWidth: this.minSupportedWidth,
                    routeName: this.$route?.name,
                    reason: this.liftReason(),
                    secondsBlocked,
                });
            },
        },
        watch: {
            isBlocked: {
                immediate: true,
                async handler(blocked) {
                    // Stop the covered page from scrolling behind the notice. Tailwind runs
                    // with `important: true`, so this beats any inline overflow a modal or
                    // drawer left on the body when the window was resized.
                    document.body.classList.toggle('overflow-hidden', blocked);
                    // App.vue marks the covered content `inert` off this. Without it the
                    // overlay is only a visual block: Tab still reaches the controls
                    // underneath, and someone who narrows the window mid-edit keeps typing
                    // into the page they can no longer see — so `aria-modal` would be
                    // claiming a containment nothing actually enforces.
                    this.$emit('update:blocked', blocked);
                    this.trackGateChange(blocked);

                    // Move focus into the panel. A focused element inside an inert subtree
                    // is silently blurred, so without this the caret is left nowhere and a
                    // keyboard user has no anchor. The panel is tabindex="-1" to receive it.
                    //
                    // Deliberately NOT paired with a restore on widen: focus lands on
                    // <body> and the user tabs from the top. Restoring the previous element
                    // needs the parent's `inert` removal to have painted first, and no
                    // amount of nextTick/rAF sequencing made that land reliably (measured
                    // 1 run in 6), so the honest behaviour is the predictable one.
                    if (!blocked) return;
                    await this.$nextTick();
                    this.$refs.panel?.focus();
                },
            },
        },
        mounted() {
            // `change` fires only when the threshold is crossed, unlike a resize listener
            // that re-runs on every frame of a window drag. Attached even while the flag is
            // off — two idle media queries — so the width is already current if the flag
            // comes on mid-session.
            this.mediaQuery.addEventListener('change', this.handleViewportChange);
            this.orientationQuery.addEventListener('change', this.handleOrientationChange);
        },
        beforeUnmount() {
            this.mediaQuery.removeEventListener('change', this.handleViewportChange);
            this.orientationQuery.removeEventListener('change', this.handleOrientationChange);
            document.body.classList.remove('overflow-hidden');
        },
    };
</script>
